import { supabase } from './supabase';
import { labResultAttachmentsService } from './labResultAttachmentsService';

export type LabResultType = 'kalprotectyna' | 'crp' | 'inne';

export interface LabResult {
  id: string;
  date: string;
  type: LabResultType;
  typeOther?: string;
  value: number;
  unit?: string;
  notes?: string;
  createdAt: string;
}

const toCamelCase = (row: Record<string, unknown>): LabResult => ({
  id: String(row.id),
  date: String(row.date),
  type: row.type as LabResultType,
  typeOther: row.type_other != null ? String(row.type_other) : undefined,
  value: Number(row.value),
  unit: row.unit != null ? String(row.unit) : undefined,
  notes: row.notes != null ? String(row.notes) : undefined,
  createdAt: String(row.created_at),
});

const DEFAULT_UNITS: Record<LabResultType, string> = {
  kalprotectyna: 'µg/g',
  crp: 'mg/L',
  inne: '',
};

export const labResultsService = {
  async getAll(userId: string): Promise<LabResult[]> {
    const { data, error } = await supabase
      .from('lab_results')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false });

    if (error) throw error;
    return (data || []).map(toCamelCase);
  },

  async createMinimal(userId: string, date?: string): Promise<LabResult> {
    const today = (date || new Date().toISOString().split('T')[0]);
    return this.create(userId, {
      date: today,
      type: 'inne',
      typeOther: 'Załącznik',
      value: 0,
    });
  },

  async create(userId: string, input: Omit<LabResult, 'id' | 'createdAt'>): Promise<LabResult> {
    const payload = {
      user_id: userId,
      date: input.date,
      type: input.type,
      type_other: input.type === 'inne' && input.typeOther ? input.typeOther : null,
      value: input.value,
      unit: input.unit ?? (input.type !== 'inne' ? DEFAULT_UNITS[input.type] : null),
      notes: input.notes || null,
    };

    const { data, error } = await supabase
      .from('lab_results')
      .insert(payload)
      .select()
      .single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async update(id: string, userId: string, input: Partial<Omit<LabResult, 'id' | 'createdAt'>>): Promise<LabResult> {
    const payload: Record<string, unknown> = {};
    if (input.date !== undefined) payload.date = input.date;
    if (input.type !== undefined) {
      payload.type = input.type;
      payload.type_other = input.type === 'inne' ? (input.typeOther ?? null) : null;
    }
    if (input.value !== undefined) payload.value = input.value;
    if (input.unit !== undefined) payload.unit = input.unit || null;
    if (input.notes !== undefined) payload.notes = input.notes || null;

    const { data, error } = await supabase
      .from('lab_results')
      .update(payload)
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async delete(id: string, userId: string): Promise<void> {
    const attachments = await labResultAttachmentsService.getByLabResultId(id);
    for (const att of attachments) {
      try {
        await labResultAttachmentsService.delete(att, userId);
      } catch (_) {
        /* ignoruj błędy storage przy usuwaniu */
      }
    }
    const { error } = await supabase
      .from('lab_results')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) throw error;
  },

  getDefaultUnit(type: LabResultType): string {
    return DEFAULT_UNITS[type];
  },
};
